import api from "./index";
import SecureSS from "../utils/SecureStorage";

/**
 * Validate if user has permission to access wallet features
 * User must have userType = "CLIENT" and role = "CLIENT_ADMIN", "CLIENT_FINANCE", or "CLIENT_COMMERCIAL_TEAM"
 * @returns {Object} { isValid: boolean, clientId: number, error: string, shouldLogout: boolean }
 */
const validateUserPermissions = () => {
  const userProfile = SecureSS.getUserProfile();

  if (!userProfile) {
    return {
      isValid: false,
      error: "User not authenticated",
      shouldLogout: true,
    };
  }

  const { userType, role, clientId } = userProfile;

  // Validate userType - if not CLIENT, user is unauthorized to access this app
  if (userType !== "CLIENT") {
    return {
      isValid: false,
      error: "Unauthorized to access",
      shouldLogout: true,
    };
  }

  // Validate role - if CLIENT but wrong role, not authorized to use the app
  const allowedRoles = [
    "CLIENT_ADMIN",
    "CLIENT_FINANCE",
    "CLIENT_COMMERCIAL_TEAM",
  ];
  if (!allowedRoles.includes(role)) {
    return {
      isValid: false,
      error: "Not authorized to use the app",
      shouldLogout: true,
    };
  }

  // Validate clientId exists
  if (!clientId) {
    return {
      isValid: false,
      error: "Client ID not found in user profile.",
      shouldLogout: true,
    };
  }

  return { isValid: true, clientId };
};

/**
 * Initialize Airwallex setup and get OAuth authorization URL
 * Calls ht-admin which wraps wallet-service /api/v1/onboard/authorize
 * @param {boolean} newToAirwallex - Whether user is new to Airwallex (true) or existing user (false)
 * @returns {Promise} Response with authorizationUrl for OAuth redirect
 */
export const initializeSetup = async (newToAirwallex = true) => {
  // Validate user permissions
  const validation = validateUserPermissions();
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  const redirectUrl = `${window.location.origin}/wallet/setup/callback`;

  return api.postRequest("/1.0/wallet/authorize", {
    clientId: validation.clientId,
    redirectUrl,
    newToAirwallex,
  });
};

/**
 * Get current wallet setup status and account details
 * Calls /1.0/wallet/account-details/{clientId}
 * @returns {Promise} Response with account details and setup status
 */
export const getSetupStatus = () => {
  // Validate user permissions and get clientId
  const validation = validateUserPermissions();
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  return api.getRequest(`/1.0/wallet/account-details/${validation.clientId}`);
};

/**
 * Get bookings with filters
 * Calls /1.0/wallet/account/bookings/{clientId} with query parameters
 * @param {Object} params - Filter parameters
 * @param {string} params.currency - Currency (required)
 * @param {string} params.query - Search query (optional)
 * @param {string} params.bookingStatus - Booking status (optional)
 * @param {string} params.bookingType - Booking type (optional)
 * @param {string} params.transactionType - Transaction type: PAYMENT or REFUND (optional)
 * @param {string} params.transactionStatus - Transaction status: PAID, OVERDUE, or REFUND (optional)
 * @param {string} params.checkInDateGt - Check-in date greater than (optional, format: YYYY-MM-DD)
 * @param {string} params.checkInDateLt - Check-in date less than (optional, format: YYYY-MM-DD)
 * @param {string} params.paymentDateGt - Payment date greater than (optional, format: YYYY-MM-DD)
 * @param {string} params.paymentDateLt - Payment date less than (optional, format: YYYY-MM-DD)
 * @param {string} params.creditDateGt - Credit date greater than (optional, format: YYYY-MM-DD)
 * @param {string} params.creditDateLt - Credit date less than (optional, format: YYYY-MM-DD)
 * @param {boolean} params.export - Set to true to export as CSV file (optional)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.size - Page size (default: 10)
 * @returns {Promise} Response with bookings data (JSON) or CSV file blob if export=true
 */
export const getBookingStatus = (params = {}) => {
  // Validate user permissions and get clientId
  const validation = validateUserPermissions();
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Build query parameters
  const queryParams = new URLSearchParams();

  // Required parameter
  if (params.currency) {
    queryParams.append("currency", params.currency);
  }

  // Optional parameters
  if (params.query) {
    queryParams.append("query", params.query);
  }
  if (params.bookingStatus) {
    queryParams.append("booking_status", params.bookingStatus);
  }
  if (params.bookingType) {
    queryParams.append("booking_type", params.bookingType);
  }
  if (params.transactionType) {
    queryParams.append("transaction_type", params.transactionType);
  }
  if (params.transactionStatus) {
    queryParams.append("transaction_status", params.transactionStatus);
  }
  if (params.checkInDateGt) {
    queryParams.append("checkin_date_gt", params.checkInDateGt);
  }
  if (params.checkInDateLt) {
    queryParams.append("checkin_date_lt", params.checkInDateLt);
  }
  if (params.paymentDateGt) {
    queryParams.append("payment_date_gt", params.paymentDateGt);
  }
  if (params.paymentDateLt) {
    queryParams.append("payment_date_lt", params.paymentDateLt);
  }
  if (params.creditDateGt) {
    queryParams.append("credit_date_gt", params.creditDateGt);
  }
  if (params.creditDateLt) {
    queryParams.append("credit_date_lt", params.creditDateLt);
  }
  if (params.export !== undefined) {
    queryParams.append("export", params.export);
  }
  if (params.page) {
    queryParams.append("page", params.page);
  }
  if (params.size) {
    queryParams.append("size", params.size);
  }

  const queryString = queryParams.toString();
  const url = `/1.0/wallet/account/bookings/${validation.clientId}${queryString ? `?${queryString}` : ""}`;

  // Use getFileRequest for CSV blob response when export is true
  if (params.export === true) {
    return api.getFileRequest(url);
  }

  return api.getRequest(url);
};
/**
 * Initialize KYC and get authorization code for Airwallex KYC component
 * Calls /1.0/wallet/init-kyc/{clientId}
 * @returns {Promise} Response with authorization_code, code_verifier, account_id
 */
export const initializeKYC = () => {
  // Validate user permissions and get clientId
  const validation = validateUserPermissions();
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  return api.getRequest(`/1.0/wallet/init-kyc/${validation.clientId}`);
};

/**
 * Check wallet balance to verify funds have been added
 * @returns {Promise} Response with balance information
 */
export const checkBalance = () => {
  return api.postRequest("/wallet/check-balance");
};

/**
 * Get the report parties (From = Hotel Trader, To = client invoice recipient)
 * for the wallet download report. ht-admin resolves the recipient from the
 * shared invoicing tables for this client + currency.
 * @param {string} currency - Account currency (required)
 * @returns {Promise} Response with { from, to } party details (to may be null)
 */
export const getReportParties = (currency) => {
  const validation = validateUserPermissions();
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  const queryString = currency
    ? `?currency=${encodeURIComponent(currency)}`
    : "";
  return api.getRequest(
    `/1.0/wallet/report-parties/${validation.clientId}${queryString}`
  );
};
